import { createClient } from '@supabase/supabase-js';

// Supabase project details - use environment variables or fallback to hardcoded values
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://fxicvtbekizhmkkeseut.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ4aWN2dGJla2l6aG1ra2VzZXV0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDczODA4NzgsImV4cCI6MjA2Mjk1Njg3OH0.MkOpO8RLqbC6coknavzwBP5ydPdTB-sWPoalMWXvQHQ';

// Create a single shared Supabase client instance
let supabaseInstance: ReturnType<typeof createClient> | null = null;

/**
 * Get or create the shared Supabase client instance
 * This prevents multiple GoTrueClient instances from being created
 */
export const getSupabaseClient = () => {
  if (!supabaseInstance) {
    // Debug logging to verify configuration
    console.log('[Supabase] Initializing client with:', {
      url: supabaseUrl,
      hasAnonKey: !!supabaseAnonKey,
      anonKeyLength: supabaseAnonKey?.length
    });
    
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('[Supabase] Missing configuration:', {
        url: supabaseUrl,
        anonKey: supabaseAnonKey ? 'present' : 'missing'
      });
      throw new Error('Supabase configuration missing. Check environment variables.');
    }
    
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true
      }
    });
  }
  return supabaseInstance;
};

/**
 * Creates a Supabase client for authenticated operations
 * Uses application-level security with user filtering
 * 
 * SECURITY: Since RLS is disabled for this client usage, it's critical that all queries
 * are properly filtered by user_id at the application level.
 * 
 * This approach works for all users as long as:
 * 1. We always filter by the authenticated user's ID
 * 2. We never trust client-side user IDs
 * 3. We get user IDs from the secure AuthContext
 */
export const createAuthenticatedSupabaseClient = () => {
  return getSupabaseClient();
};

/**
 * Hook to get an authenticated Supabase client
 * 
 * CRITICAL SECURITY REQUIREMENT:
 * - Always use this with the user ID from useAuth() context
 * - Never trust user IDs from props or parameters
 * - Always filter queries: .eq('user_id', authenticatedUserId)
 */
export const useAuthenticatedSupabase = () => {
  return getSupabaseClient();
};

// Create and export the standard authenticated client
export const supabase = getSupabaseClient();
